import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  WERKSTATUUT, VERVOER, RIJBEWIJZEN, WERKMATERIAAL,
  SPEC_MONTAGE, SPEC_AFWERKING, TALEN,
} from "@/lib/monteurOptions";

function Section({ title, children }) {
  return (
    <div className="space-y-3">
      <p className="label-caps border-b border-slate-200 pb-2">{title}</p>
      {children}
    </div>
  );
}

function RadioRow({ name, options, value, onChange, testid }) {
  return (
    <div className="space-y-2">
      {options.map((o) => (
        <label key={o.value} className="flex items-start gap-2.5 cursor-pointer text-sm text-slate-700">
          <input
            type="radio"
            name={name}
            data-testid={`${testid}-${o.value}`}
            checked={value === o.value}
            onChange={() => onChange(o.value)}
            className="mt-0.5 w-4 h-4 accent-orange-500 flex-shrink-0"
          />
          <span>{o.label}</span>
        </label>
      ))}
    </div>
  );
}

function CheckGroup({ options, values, onToggle, testid, asObjects }) {
  const list = asObjects ? options : options.map((o) => ({ value: o, label: o }));
  return (
    <div className="grid sm:grid-cols-2 gap-2">
      {list.map((o) => (
        <label key={o.value} className="flex items-center gap-2.5 cursor-pointer text-sm text-slate-700">
          <input
            type="checkbox"
            data-testid={`${testid}-${o.value}`}
            checked={(values || []).includes(o.value)}
            onChange={() => onToggle(o.value)}
            className="w-4 h-4 accent-orange-500 flex-shrink-0"
          />
          <span>{o.label}</span>
        </label>
      ))}
    </div>
  );
}

export default function MonteurFields({ data, onChange }) {
  const upd = (patch) => onChange({ ...data, ...patch });
  const toggle = (key, val) => {
    const arr = data[key] || [];
    upd({ [key]: arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val] });
  };

  const inputCls = "rounded-sm border-slate-300";

  return (
    <div className="space-y-6">
      <Section title="Werkstatuut">
        <RadioRow name="werkstatuut" testid="werkstatuut" options={WERKSTATUUT}
          value={data.werkstatuut} onChange={(v) => upd({ werkstatuut: v })} />
      </Section>

      <Section title="Vervoer">
        <RadioRow name="vervoer" testid="vervoer" options={VERVOER}
          value={data.vervoer} onChange={(v) => upd({ vervoer: v })} />
        <label className="flex items-center gap-2.5 cursor-pointer text-sm text-slate-700 pt-1">
          <input type="checkbox" data-testid="rijbewijs-europa"
            checked={!!data.rijbewijs_europa}
            onChange={(e) => upd({ rijbewijs_europa: e.target.checked })}
            className="w-4 h-4 accent-orange-500" />
          <span>Ik heb een geldig rijbewijs, geldig in alle Europese landen</span>
        </label>
        <div>
          <Label className="text-xs font-bold text-slate-700 mb-1.5 block">Welk rijbewijs?</Label>
          <CheckGroup options={RIJBEWIJZEN} values={data.rijbewijs_categorieen}
            onToggle={(v) => toggle("rijbewijs_categorieen", v)} testid="rijbewijs-cat" />
        </div>
      </Section>

      <Section title="Werkmateriaal">
        <RadioRow name="werkmateriaal" testid="werkmateriaal" options={WERKMATERIAAL}
          value={data.werkmateriaal} onChange={(v) => upd({ werkmateriaal: v })} />
      </Section>

      <Section title="Mijn specialiteiten — Montage">
        <CheckGroup options={SPEC_MONTAGE} asObjects values={data.spec_montage}
          onToggle={(v) => toggle("spec_montage", v)} testid="spec-montage" />
        <Input data-testid="spec-montage-andere" value={data.spec_montage_andere || ""}
          onChange={(e) => upd({ spec_montage_andere: e.target.value })}
          className={inputCls} placeholder="Andere montage (optioneel)" />
      </Section>

      <Section title="Mijn specialiteiten — Binnenlijst afwerking">
        <CheckGroup options={SPEC_AFWERKING} asObjects values={data.spec_afwerking}
          onToggle={(v) => toggle("spec_afwerking", v)} testid="spec-afwerking" />
        <Input data-testid="spec-afwerking-andere" value={data.spec_afwerking_andere || ""}
          onChange={(e) => upd({ spec_afwerking_andere: e.target.value })}
          className={inputCls} placeholder="Kleine verbouwing, bv. raamopening vergroten (optioneel)" />
      </Section>

      <Section title="Leidinggeven">
        <RadioRow name="leiding_team" testid="leiding"
          options={[{ value: "ja", label: "Ja, ik kan leiding geven aan een team" }, { value: "nee", label: "Nee" }]}
          value={data.leiding_team === true ? "ja" : data.leiding_team === false ? "nee" : undefined}
          onChange={(v) => upd({ leiding_team: v === "ja" })} />
      </Section>

      <Section title="Talen">
        <CheckGroup options={TALEN} values={data.talen}
          onToggle={(v) => toggle("talen", v)} testid="taal" />
        <div className="grid sm:grid-cols-2 gap-3 pt-1">
          <Input data-testid="moedertaal" value={data.moedertaal || ""}
            onChange={(e) => upd({ moedertaal: e.target.value })}
            className={inputCls} placeholder="Moedertaal" />
          <Input data-testid="andere-talen" value={data.andere_talen || ""}
            onChange={(e) => upd({ andere_talen: e.target.value })}
            className={inputCls} placeholder="Andere talen" />
        </div>
      </Section>

      <Section title="Over jou">
        <Textarea data-testid="bio-field" value={data.bio || ""}
          onChange={(e) => upd({ bio: e.target.value })} rows={4}
          className={inputCls} placeholder="Extra informatie over jezelf of je werkskills..." />
      </Section>
    </div>
  );
}
